<template>
  <span :aria-label="ariaLabel" :class="classes">
    <template v-if="!dot">
      <slot>{{ display }}</slot>
    </template>
  </span>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { cva, type VariantProps } from 'class-variance-authority';

const badgeVariants = cva('inline-flex items-center justify-center rounded-full font-semibold', {
  variants: {
    tone: {
      default: 'bg-paper-muted text-ink',
      accent: 'bg-accent text-ink-inverse',
      danger: 'bg-danger-500 text-ink-inverse',
    },
    size: {
      sm: 'h-4 min-w-4 px-1 text-[10px]',
      md: 'h-5 min-w-5 px-1.5 text-xs',
      lg: 'h-6 min-w-6 px-2 text-sm',
      xl: 'h-7 min-w-7 px-2.5 text-base',
      xxl: 'h-8 min-w-8 px-3 text-lg',
      xxxl: 'h-9 min-w-9 px-3.5 text-xl',
      xxxxl: 'h-10 min-w-10 px-4 text-2xl',
      xxxxxl: 'h-11 min-w-11 px-4.5 text-3xl',
      xxxxxxl: 'h-12 min-w-12 px-5 text-4xl',
      xxxxxxxxl: 'h-13 min-w-13 px-5.5 text-5xl',
    },
    dot: {
      true: '',
      false: '',
    },
  },
  compoundVariants: [
    { dot: true, size: 'sm', class: 'h-2 w-2 min-w-0 p-0' },
    { dot: true, size: 'md', class: 'h-2.5 w-2.5 min-w-0 p-0' },
  ],
  defaultVariants: {
    tone: 'default',
    size: 'md',
    dot: false,
  },
});

export type BadgeTone = NonNullable<VariantProps<typeof badgeVariants>['tone']>;
export type BadgeSize = NonNullable<VariantProps<typeof badgeVariants>['size']>;

interface Props {
  tone?: BadgeTone;
  size?: BadgeSize;
  count?: number | string | null;
  max?: number;
  dot?: boolean;
  ariaLabel?: string;
}

const props = withDefaults(defineProps<Props>(), {
  tone: 'default',
  size: 'md',
  count: null,
  max: 99,
  dot: false,
});

const classes = computed(() =>
  badgeVariants({ tone: props.tone, size: props.size, dot: props.dot }),
);

const display = computed(() => {
  if (props.count === null) return '';
  if (typeof props.count === 'number' && props.count > props.max) return `${props.max}+`;
  return String(props.count);
});
</script>
